/**
 * Safe renderer for assistant-emitted chat content (issue #215).
 *
 * TSAI parity goal
 * ----------------
 * TSAI's `sanitizeAssistantHtml` in `public/widget/uiController.js`:
 *   1. Auto-links bare URLs (so `Visit https://example.com` becomes a real link).
 *   2. Honors AI-provided `<a>` tags but normalizes them to `target="_blank"`
 *      + safe `rel`.
 *   3. Escapes / strips raw HTML the model wasn't supposed to emit.
 *
 * The previous Engage renderer dropped to `<p>{text}</p>` — safe from XSS but
 * fails (1) (bare URLs render as plain text) and (2) (Markdown links render
 * as `[text](url)` literals). This component closes that parity gap.
 *
 * Implementation choice: `react-markdown@10` + `remark-gfm@4`.
 *
 *   - `remark-gfm` handles bare-URL → `<a>` (autolink literals) AND
 *     Markdown `[text](url)` syntax.
 *   - `react-markdown` escapes every raw HTML byte by default (no
 *     `rehype-raw`, no `dangerouslySetInnerHTML`). A `<script>` in model
 *     output renders as visible text — safe.
 *   - Allowed-elements list explicitly restricts the rendered subset to chat-
 *     appropriate tags. Headings, blockquotes, images, tables, hr — all
 *     dropped (assistant has no business emitting them inside a 320×500 chat
 *     panel).
 *   - The custom `a` component routes every href through
 *     `getSafeAnchorAttrs` (see `lib/widget/safe-assistant-link.ts`) so unsafe
 *     schemes (`javascript:`, `data:`, etc.) and relative paths collapse to
 *     plain text — same policy as TSAI.
 *
 * The pure URL-filter policy is unit-tested in
 * `lib/widget/safe-assistant-link.test.ts` (10 cases across the parity
 * acceptance matrix from #215). The React render is a thin wrapper.
 */

import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { getSafeAnchorAttrs } from "@/lib/widget/safe-assistant-link";

const ASSISTANT_ALLOWED_ELEMENTS: ReadonlyArray<string> = [
  "p",
  "a",
  "br",
  "strong",
  "em",
  "code",
  "ul",
  "ol",
  "li",
  "pre",
];

const ASSISTANT_COMPONENTS: Components = {
  p: ({ children }) => <p className="whitespace-pre-wrap break-words">{children}</p>,
  a: ({ href, children }) => {
    const safe = getSafeAnchorAttrs(href);
    if (safe.href === null) {
      // Unsafe href: collapse to plain text. The anchor is dropped entirely
      // (rather than rendered with no href) so screen-readers don't announce
      // an unreachable link.
      return <>{children}</>;
    }
    return (
      <a
        href={safe.href}
        target={safe.target}
        rel={safe.rel}
        className="break-words underline underline-offset-2 hover:no-underline"
      >
        {children}
      </a>
    );
  },
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  code: ({ children }) => (
    <code className="rounded bg-zinc-100 px-1 py-0.5 text-[0.85em] dark:bg-zinc-800/80">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded bg-zinc-100 p-2 text-[0.85em] dark:bg-zinc-800/80">
      {children}
    </pre>
  ),
  ul: ({ children }) => <ul className="ml-4 list-disc">{children}</ul>,
  ol: ({ children }) => <ol className="ml-4 list-decimal">{children}</ol>,
  li: ({ children }) => <li className="my-0.5">{children}</li>,
};

export interface AssistantMessageContentProps {
  readonly text: string;
}

export function AssistantMessageContent({ text }: AssistantMessageContentProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      allowedElements={ASSISTANT_ALLOWED_ELEMENTS as string[]}
      unwrapDisallowed
      components={ASSISTANT_COMPONENTS}
    >
      {text}
    </ReactMarkdown>
  );
}
